import React, {useState, useEffect} from 'react';
import {UserModelV2, StandardSetCreatorV1} from '@albert-io/models';
import {resource} from '@albert-io/json-api-framework/request/builder';
import {invalidatePartialInterest} from 'resources/mandark.resource';
import awaitMandarkQueries from 'lib/hocs/awaitMandarkQueries';
import sessionStore from 'client/Session/SessionStore';
import classnames from 'classnames';

import {
  Radio,
  Card,
  Label,
  Text,
  Select,
  Button,
  Checkbox,
  addGenericErrorToast,
  addToast
} from '@albert-io/atomic';

import {List} from 'immutable';

import {completeContentPersonalization} from 'lib/OnboardingUtil/OnboardingUtil';

import {getUserStateName, isUserFromUSA} from './ContentPersonalization.util';
import './content-personalization.scss';

// default ids to preselect for users who are onboarding
const ONBOARDING_DEFAULT_CHECKED_IDS = [
  '96ffccd1-450f-43f7-a15c-160bd34f6348', // ACT® WorkKeys
  'fdc31a6c-0651-4c1f-82ae-4dfade4e6f48', // College Board (SAT®)
  'b274fd95-6d13-410b-b9c2-ca4515b693de', // ACT®
  'ae3994ee-2392-4469-98a7-37eccab9d5d1', // College Board (Pre-AP®)
  '8aa036cb-5b93-40e6-8b52-3de9d9305496' // College Board (AP®)
];

interface Props {
  user: UserModelV2;
  isOnboarding: boolean;
  onComplete: () => void; // for onboarding only, not used in settings menu. see ForceOnboarding.react.js
  className: string;
}

const allPreferencesQuery = resource('standard_set_creators_v1')
  .mandarkEndpoint(['standard_set_creators_v1'])
  .pageSize(200);

const ContentPersonalization = ({
  user,
  isOnboarding = false,
  onComplete: onOnboardingComplete = () => {},
  className
}: Props) => {
  const [showStateSelector, setShowStateSelector] = useState(isUserFromUSA(user));

  // all standard creators fetched from server to be displayed
  const [statePreferences, setStatePreferences] = useState<List<StandardSetCreatorV1>>(List());
  const [nationalPreferences, setNationalPreferences] = useState<List<StandardSetCreatorV1>>(
    List()
  );
  // IDs of the actual standard creators the user has selected
  const [userNationalIds, setUserNationalIds] = useState<Set<string>>(
    isOnboarding
      ? List(ONBOARDING_DEFAULT_CHECKED_IDS).toSet()
      : user
          .getStandardSetCreators()
          .filter((preference) => preference.type === 'national' || preference.type === 'internal')
          .map((standardSetCreator) => standardSetCreator.getId())
          .toSet()
  );

  // ID for the ONE state standard creator the user can select from the dropdown
  const [userStateId, setUserStateId] = useState<string>();

  // fetch all national standards on page load
  useEffect(() => {
    allPreferencesQuery.getResourcePromise().then((preferences) => {
      // group preference results by state or national
      const groupedPreferences = preferences.groupBy((preference) => preference.type);
      setNationalPreferences(
        // with national preferences, we're including the internal Albert standards
        groupedPreferences.get('national').concat(groupedPreferences.get('internal', List())).sort()
      );
      // sort state standard creators before setting value so they show up in the dropdown correctly
      setStatePreferences(groupedPreferences.get('state').sortBy((preference) => preference.name));
    });
  }, []);

  useEffect(() => {
    const userStateName = getUserStateName(user);
    if (userStateName) {
      const userState = statePreferences.find(
        (preference) => preference.getName() === userStateName
      );
      setUserStateId(userState?.getId());
    }
  }, [statePreferences, user]);

  const saveUserPreferences = async () => {
    const preferencesToAdd = List(userNationalIds).toJS();
    if (showStateSelector) {
      preferencesToAdd.push(userStateId);
    }
    try {
      await user
        .replaceRelationship({type: 'standard_set_creators_v1', relation: preferencesToAdd})
        .save();
      addToast({
        type: 'success',
        message: 'Preferences saved'
      });
      invalidatePartialInterest({resourcePath: ['users_v2', sessionStore.getUserId()]});
      if (isOnboarding) {
        await completeContentPersonalization(user).save();
        onOnboardingComplete();
      }
    } catch (error) {
      console.error(error); // eslint-disable-line no-console
      addGenericErrorToast();
    }
  };

  return (
    <>
      {isOnboarding && (
        <div className='u-margin_auto u-mar-b_4 u-text-align_center'>
          <Text className='u-display_block' bold size='xl'>
            Verify Your Teacher Preferences
          </Text>
          <Text emphasis='lowest'>We&apos;ll help you find relevant content faster</Text>
        </div>
      )}
      <Card
        paddingLevel={5}
        className={classnames(className, {
          'content-personalization-body__onboarding': isOnboarding
        })}
      >
        <Text className='u-display_block u-mar-b_2'>Do you teach in the United States?</Text>
        <Label htmlFor='from-usa'>
          <Radio
            name='from-usa'
            className='u-mar-r_1'
            onChange={() => setShowStateSelector(true)}
            checked={showStateSelector}
          />
          Yes
        </Label>
        <Label htmlFor='not-from-usa' className='u-mar-l_3'>
          <Radio
            name='not-from-usa'
            className='u-mar-r_1'
            onChange={() => setShowStateSelector(false)}
            checked={!showStateSelector}
          />
          No
        </Label>
        {showStateSelector && (
          <>
            <Text className='u-display_block u-mar-b_2 u-mar-t_4'>
              In which state do you teach?
            </Text>
            <Select
              value={userStateId}
              onChange={(e) => setUserStateId(e.target.value)}
              data-testid='personalization__standard-state-dropdown'
            >
              {statePreferences.map((preference) => (
                <option key={preference.getName()} value={preference.getId()}>
                  {preference.getName()}
                </option>
              ))}
            </Select>
          </>
        )}
        <Text className='u-display_block u-mar-t_4 u-mar-b_2'>Select any that you teach:</Text>
        {nationalPreferences.map((preference) => (
          <div className='u-mar-b_1' key={preference.getId()}>
            <Checkbox
              className='u-mar-r_2 personalization__standard-publisher-checkbox'
              data-testid={`personalization__national-checkbox__${preference.getName()}`}
              label={preference.getName()}
              checked={userNationalIds.has(preference.getId())}
              onChange={() =>
                setUserNationalIds(
                  (prevIds) =>
                    (prevIds.has(preference.getId())
                      ? prevIds.delete(preference.getId())
                      : prevIds.add(preference.getId())) as Set<string>
                )
              }
            />
            <Text>{preference.getName()}</Text>
          </div>
        ))}
      </Card>
      <div
        className={classnames(
          'u-display_flex u-justify-content_flex-end',
          {
            'content-personalization-body__onboarding': isOnboarding
          },
          'u-mar-t_4'
        )}
      >
        <Button
          data-testid='personalization__submit-changes-btn'
          onClick={saveUserPreferences}
          className={classnames('u-mar-b_4', {
            'content-peronsalization-button__onboarding': isOnboarding
          })}
        >
          Save Preferences
        </Button>
      </div>
    </>
  );
};

export default awaitMandarkQueries(
  () => ({
    queries: {
      user: resource('users_v2')
        .mandarkEndpoint(['users_v2', sessionStore.getUserId()])
        .include('standard_set_creators_v1,school_v5')
    }
  }),
  ContentPersonalization
);
